import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Table, TableBody, TableRow, TableCell, TableContainer, TableHead, Typography, Chip, Tooltip, Divider, LinearProgress, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Crumbs, DataRow, DateDisplay, MonoText, Title, TraxxButton } from 'app/components';
import { API } from 'app/services';
import { snakeToTitle, useAsyncTask, useRouter } from 'app/utils';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { Link as BrowserLink } from 'react-router-dom';
import { DownloadRegtankPreview } from './components';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3, 3, 8) },
  table: { marginTop: 14, borderRadius: 5 },
  title: { marginTop: 15, textDecoration: 'underline' },
  head: { fontWeight: 'bold', maxWidth: 200 },
  subtitle: { opacity: '70%' },
  tag: { margin: '2px' },
  tooltip: { fontSize: '1rem' },
}));

// ---------------------------------------------------------------------------
// Keyword chip config
// ---------------------------------------------------------------------------
const KEYWORD_CONFIG = {
  PEP: { label: 'Political Exposed Person', color: '#1976d2' },
  SAN: { label: 'Sanction', color: '#d32f2f' },
  SI: { label: 'Special Interest', color: '#7b1fa2' },
  AM: { label: 'Adverse Media', color: '#ed6c02' },
  FR: { label: 'Financial Regulator', color: '#2e7d32' },
  LF: { label: 'Law Enforcement', color: '#d32f2f' },
  POI: { label: 'Profile of Interest', color: '#607d8b' },
  OOL: { label: 'Other Official Lists', color: '#00acc1' },
};

const KeywordChip = ({ code, classes }) => {
  const config = KEYWORD_CONFIG[code] || {};
  return (
    <Tooltip title={config.label || code} classes={{ tooltip: classes.tooltip }}>
      <Chip label={code} size="small" className={classes.tag} style={{ backgroundColor: config.color || '#9e9e9e', color: 'white' }} />
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Reusable connections table (linked individuals / linked businesses / connections)
// ---------------------------------------------------------------------------
const ConnectionsTable = ({ title, rows, classes }) => {
  if (!rows?.length) return null;
  return (
    <Grid item xs={12}>
      <TableContainer component={Paper} className={classes.table}>
        <Title prefix={title} className={classes.title} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Name</strong>
              </TableCell>
              <TableCell>
                <strong>URL</strong>
              </TableCell>
              <TableCell>
                <strong>Association</strong>
              </TableCell>
              <TableCell>
                <strong>Keywords</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((p, i) => (
              <TableRow key={i} hover>
                <TableCell>{p.name || '-'}</TableCell>
                <TableCell>
                  {p.url ? (
                    <a href={p.url} target="_blank" rel="noopener noreferrer">
                      {p.url}
                    </a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{p.associations || '-'}</TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {p.keywords?.length > 0 ? p.keywords.map((kw, j) => <KeywordChip key={j} code={kw} classes={classes} />) : '-'}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

// ---------------------------------------------------------------------------
// Heading detection for profile notes
// ---------------------------------------------------------------------------
const isHeading = (p) => {
  if (!p) return false;
  const clean = p.trim();
  if (!clean || clean.length > 120) return false;
  if (clean === clean.toUpperCase()) return true;
  return clean.split(' ').some((w) => w.length > 3 && w === w.toUpperCase());
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
let mounted = false;

function MatchDetailRegtank({ routeType = 'account' }) {
  const classes = useStyles();
  const router = useRouter();
  const { params } = router.match;
  const { enqueueSnackbar } = useSnackbar();

  const access = useSelector((state) => state.profile.access);
  const loading = useSelector((state) => state.layout.loadingTasks);

  const [errorCatcher] = useAsyncTask('getScreening');
  const [runUnresolve] = useAsyncTask('unresolve');

  const [matching, setMatching] = useState(null);
  const [matchList, setMatchListing] = useState();
  const [matchRawResult, setMatchRawResult] = useState(null);
  const [printPreview, setPrintPreview] = useState(false);

  const { screening_id, match_id } = params;

  useEffect(() => {
    mounted = true;
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (access?.token) getMatchDetail();
  }, [access]);

  // -------------------------------------------------------------------------
  const getMatchDetail = () => {
    if (!screening_id || !match_id) return enqueueSnackbar('invalid id', { variant: 'error' });

    errorCatcher(async () => {
      const loader = routeType === 'account' ? API.Screening.get_match_detail : API.Admin.get_match;

      const { model } = await loader(screening_id, match_id);
      if (!mounted) return;

      setMatching(model);

      const { content, aml_types, lists, media, peps, sanctions } = model.screen_result_raw || {};

      const parsed = content ? JSON.parse(content) : {};
      const match = parsed.match || {};
      const matchDetails = parsed.matchDetails || {};
      const parsedPeps = peps ? JSON.parse(peps) : {};

      setMatchRawResult({
        detail: {
          profile: {
            matching_name: match.name,
            person: {
              names: [{ name: match.name }],
              aliases: parsedPeps.aliases || [],
              nationalities: match.nationality ? [match.nationality] : [],
              dates_of_birth: match.dayOfBirth ? [{ value: `${match.dayOfBirth}-${match.monthOfBirth}-${match.yearOfBirth}`, source: 'Regtank' }] : [],
              addresses: parsedPeps.addresses || [],
              deceased: parsedPeps.deceased || false,
              reportedAllegation: parsedPeps.reportedAllegation || null,
              associates: (matchDetails.connections || []).map((c) => ({
                type: c.associations || 'Associate',
                name: c.name,
                keywords: c.keywords || [],
              })),
              profileNotes: parsedPeps.profileNotes || [],
            },
          },
        },
        media: media ? JSON.parse(media) : [],
        lists: lists ? JSON.parse(lists) : [],
        peps: parsedPeps,
        sanctions: sanctions ? JSON.parse(sanctions) : [],
        aml_types: aml_types ? JSON.parse(aml_types) : [],
      });
    });
  };

  // -------------------------------------------------------------------------
  const getScreenDetailPath = (id) => (routeType === 'admin' ? `/admin/screenings/${id}/detail` : `/screenings/${id}/detail`);

  const getCaseDetailPath = (id) => (routeType === 'admin' ? `/admin/screenings/cases/${id}/detail` : `/screenings/cases/${id}/detail`);

  const unResolve = () => {
    runUnresolve(async () => {
      await API.Users.unresolve_matches(screening_id, { resolve_ids: [match_id] });
      enqueueSnackbar('Match unresolved', { variant: 'success' });
      getMatchDetail();
    });
  };

  // -------------------------------------------------------------------------
  return (
    <Box className={classes.root}>
      {!printPreview && (
        <>
          {/* BREADCRUMBS */}
          <Crumbs
            current="Detail"
            links={[
              {
                text: 'Screening',
                href: routeType === 'admin' ? '/admin/screenings' : '/screenings',
                key: 'screening',
              },
              {
                text: params.screening_id,
                href: getScreenDetailPath(params.screening_id),
                key: 'screeningId',
              },
            ]}
          />

          <Title
            prefix="Match detail"
            floatButton={
              matching?.profile_retrieved_at && {
                onClick: () => setPrintPreview(true),
                text: 'Preview print document',
              }
            }>
            {matching && 'Powered by Regtank'}
          </Title>

          <Grid spacing={2} container>
            {/* ── LEFT PANEL: match metadata ─────────────────────────────── */}
            <Grid item md={6} xs={12}>
              <TableContainer component={Paper} className={classes.table}>
                {loading.getScreening && <LinearProgress />}
                {!loading.getScreening && matching && (
                  <Table>
                    <TableBody>
                      <DataRow name="Name" value={matching.name} />
                      <DataRow name="Match rating" value={snakeToTitle(matching.match_rating)} />

                      {(matching.screen?.type === 'simple' || matching.screen?.type === 'passport') && <DataRow name="Gender" value={snakeToTitle(matching.gender || '')} />}

                      <DataRow name="Created date" value={<DateDisplay date={matching.created_at} />} />
                      <DataRow name="Match ID" value={<MonoText>{matching.reference}</MonoText>} />

                      <DataRow
                        name="Screening ID"
                        value={
                          <Link underline="always" component={BrowserLink} to={getScreenDetailPath(matching.screen?.id)}>
                            <MonoText>{matching.screen?.reference}</MonoText>
                          </Link>
                        }
                      />

                      {matching.screen?.case && (
                        <DataRow
                          name="Case ID"
                          value={
                            <Link underline="always" component={BrowserLink} to={getCaseDetailPath(matching.screen.case.id)}>
                              <MonoText>{matching.screen.case.reference}</MonoText>
                            </Link>
                          }
                        />
                      )}

                      <DataRow name="Resolved" value={<DateDisplay date={matching.resolved_at} />} />

                      {matching.resolved_reason && matching.resolved_at && <DataRow name="Resolved reason" value={matching.resolved_reason} />}

                      {matching.resolved_at && routeType !== 'admin' && (
                        <DataRow
                          name=""
                          value={
                            <TraxxButton loading={loading.unresolve} onSubmit={unResolve}>
                              <Typography>Unresolve</Typography>
                            </TraxxButton>
                          }
                        />
                      )}

                      {matching.profile_retrieved_at && <DataRow name="Profile retrieved date" value={<DateDisplay date={matching.profile_retrieved_at} />} />}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            </Grid>

            {/* ── RIGHT PANEL: key information ───────────────────────────── */}
            <Grid item md={6} xs={12}>
              <TableContainer component={Paper} className={classes.table}>
                <Title prefix="Key Information" className={classes.title} />
                <Table>
                  <TableBody>
                    {/* Person fields */}
                    <DataRow name="Name" value={matchRawResult?.detail?.profile?.matching_name} />
                    <DataRow name="Aliases" value={matchRawResult?.detail?.profile?.person?.aliases?.length > 0 ? matchRawResult.detail.profile.person.aliases.join(', ') : '-'} />

                    {matchRawResult?.detail?.profile?.person?.addresses?.length > 0 && (
                      <TableRow>
                        <TableCell>
                          <Typography className={classes.head}>Address</Typography>
                          {matchRawResult.detail.profile.person.addresses.map((addr, i) => (
                            <Typography key={i}>{[addr.city, addr.stateOrRegion, addr.countryOrTerritory].filter(Boolean).join(', ')}</Typography>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}

                    {matchRawResult?.detail?.profile?.person?.dates_of_birth?.length > 0 && <DataRow name="Date of Birth" value={matchRawResult.detail.profile.person.dates_of_birth.map((d) => d.value).join(', ')} />}

                    {matchRawResult?.peps?.otherRecordedDatesOfBirth?.length > 0 && (
                      <TableRow>
                        <TableCell>
                          <Typography className={classes.head}>Other DOB</Typography>
                          {matchRawResult.peps.otherRecordedDatesOfBirth.map((dob, i) => (
                            <Typography key={i}>
                              {dob.day}/{dob.month}/{dob.year}
                            </Typography>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}

                    {matchRawResult?.detail?.profile?.person?.reportedAllegation && <DataRow name="Reported Allegation" value={matchRawResult.detail.profile.person.reportedAllegation} />}

                    {matchRawResult?.peps?.jurisdiction && <DataRow name="Jurisdiction" value={matchRawResult.peps.jurisdiction} />}

                    {matchRawResult?.peps?.deceased && <DataRow name="Deceased" value="Yes" />}

                    {matchRawResult?.lists?.placeOfBirth && <DataRow name="Place of Birth" value={matchRawResult.lists.placeOfBirth} />}

                    {matchRawResult?.lists?.countryOfResidence && <DataRow name="Country of Residence" value={matchRawResult.lists.countryOfResidence} />}

                    {matchRawResult?.lists?.nationality && <DataRow name="Nationality" value={`${matchRawResult.lists.nationality}${matchRawResult.lists.nationalityCode ? ` (${matchRawResult.lists.nationalityCode})` : ''}`} />}

                    {matchRawResult?.lists?.gender && <DataRow name="Gender" value={matchRawResult.lists.gender} />}

                    {/* Organisation / business fields */}
                    {matchRawResult?.lists?.businessName && <DataRow name="Business Name" value={matchRawResult.lists.businessName} />}

                    {matchRawResult?.lists?.countryOfIncorporation && <DataRow name="Country of Incorporation" value={matchRawResult.lists.countryOfIncorporation} />}

                    {matchRawResult?.peps?.dayOfIncorporation && <DataRow name="Date of Incorporation" value={`${matchRawResult.peps.dayOfIncorporation}-${matchRawResult.peps.monthOfIncorporation}-${matchRawResult.peps.yearOfIncorporation}`} />}

                    {matchRawResult?.peps?.natureOfBusiness?.length > 0 && <DataRow name="Nature of Business" value={matchRawResult.peps.natureOfBusiness.join(', ')} />}

                    {matchRawResult?.peps?.identificationDetails?.length > 0 && (
                      <TableRow>
                        <TableCell>
                          <Typography className={classes.head}>Identification Details</Typography>
                          {matchRawResult.peps.identificationDetails.map((id, i) => (
                            <Typography key={i}>
                              {id.type}: <strong>{id.value}</strong>
                            </Typography>
                          ))}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* Risk type chips */}
                    <TableRow>
                      <TableCell>
                        <Typography className={classes.head}>Risk Type</Typography>
                        {matchRawResult?.aml_types?.map((aml, i) => (
                          <KeywordChip key={i} code={aml} classes={classes} />
                        ))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* ── CONNECTIONS / ASSOCIATES ───────────────────────────────── */}
            <ConnectionsTable title="Connections" rows={matchRawResult?.lists?.connections} classes={classes} />
            <ConnectionsTable title="Linked Individuals" rows={matchRawResult?.lists?.linkedIndividuals} classes={classes} />
            <ConnectionsTable title="Linked Businesses" rows={matchRawResult?.lists?.linkedBusinesses} classes={classes} />

            {/* ── MEDIA ──────────────────────────────────────────────────── */}
            {matchRawResult?.media?.length > 0 && (
              <Grid item xs={12}>
                <TableContainer component={Paper} className={classes.table}>
                  <Title prefix="Media" className={classes.title} />
                  <Table>
                    <TableBody>
                      {matchRawResult.media.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Link href={item.externalLink} target="_blank" rel="noopener noreferrer">
                              {item.title || item.externalLink}
                            </Link>
                            {item.title && (
                              <Typography variant="caption" display="block" style={{ color: 'gray' }}>
                                {item.externalLink}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* ── PROFILE NOTES ──────────────────────────────────────────── */}
            {matchRawResult?.detail?.profile?.person?.profileNotes?.length > 0 && (
              <Grid item xs={12}>
                <TableContainer component={Paper} className={classes.table}>
                  <Title prefix="Profile Notes" className={classes.title} />
                  <Table>
                    <TableBody>
                      {matchRawResult.detail.profile.person.profileNotes.map((note, i) => {
                        const paragraphs = note
                          .split('\n')
                          .map((p) => p.trim())
                          .filter(Boolean);
                        return (
                          <TableRow key={i}>
                            <TableCell>
                              <Typography style={{ fontSize: 20, fontWeight: 600, marginBottom: 10, color: '#9c27b0' }}>Note {i + 1}:</Typography>
                              {paragraphs.map((p, idx) => {
                                const heading = isHeading(p);
                                return (
                                  <React.Fragment key={idx}>
                                    {heading && idx !== 0 && <Divider />}
                                    <Typography style={{ fontWeight: heading ? 600 : 400, color: heading ? '#1976d2' : '#333', marginBottom: 8, lineHeight: 1.6 }}>{p}</Typography>
                                  </React.Fragment>
                                );
                              })}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* ── SANCTIONS ──────────────────────────────────────────────── */}
            {matchRawResult?.lists && (
              <Grid item xs={12}>
                <TableContainer component={Paper} className={classes.table}>
                  <Title prefix="Sanctions" className={classes.title} />
                  <Table>
                    <TableBody>
                      {matchRawResult.lists.identificationInformationList?.length > 0 && (
                        <>
                          <TableRow>
                            <TableCell>
                              <Typography className={classes.head}>Identification Information</Typography>
                            </TableCell>
                          </TableRow>
                          {matchRawResult.lists.identificationInformationList.map((id, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Typography>
                                  {id.type}: <strong>{id.number}</strong>
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}

                      {matchRawResult.lists.referenceList?.length > 0 && (
                        <>
                          <TableRow>
                            <TableCell>
                              <Typography className={classes.head}>Reference List</Typography>
                            </TableCell>
                          </TableRow>
                          {matchRawResult.lists.referenceList.map((ref, i) => (
                            <TableRow key={i}>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <KeywordChip code={ref.keyword} classes={classes} />
                                  <Typography>{ref.category}</Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}

                      {matchRawResult.lists.images?.length > 0 && (
                        <>
                          <TableRow>
                            <TableCell>
                              <Typography className={classes.head}>Images</Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <Box display="flex" flexWrap="wrap" gap={1}>
                                {matchRawResult.lists.images
                                  .filter((img) => img && !img.includes('47news.jp'))
                                  .map((img, i) => (
                                    <Box key={i} width={120} height={120} style={{ borderRadius: 6, overflow: 'hidden', background: '#f5f5f5' }}>
                                      <img
                                        src={img}
                                        alt={`Image ${i + 1}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    </Box>
                                  ))}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}
          </Grid>
        </>
      )}
      {printPreview && (
        <DownloadRegtankPreview
          matchList={matchList}
          cancelPrint={() => setPrintPreview(false)}
          detail={matching}
          profile={{
            details: matchRawResult,
            names: matchRawResult?.detail?.profile?.person,
          }}
        />
      )}
    </Box>
  );
}

export default MatchDetailRegtank;
